import { useEffect, useState } from "react";
import { Plus, X, Trash2, Wrench, Package } from "lucide-react";
import api from "../lib/apiClient";
import { fmtCAD } from "../lib/format";

/**
 * Line items editor — Services and Materials rendered as two distinct
 * sub-sections (Item 4b invoice layout parity). The underlying data model
 * remains a single flat `line_items` array; we visually group by
 * `category` on each item ("service" by default, "material" otherwise).
 *
 * External behaviour unchanged: totals, discount, and tax calc still
 * operate on all items together, and the ordering inside the array is
 * preserved (services first, then materials).
 */
export function LineItemsEditor({ lineItems, setLineItems, invDiscType, setInvDiscType, invDiscVal, setInvDiscVal, hstRate, brandColor }) {
  const brand = brandColor || "#0F4C81";
  const [pricebook, setPricebook] = useState([]);
  const [pickerCategory, setPickerCategory] = useState(null); // null | "service" | "material"
  const [search, setSearch] = useState("");

  useEffect(() => { api.get("/pricebook").then(({ data }) => setPricebook(data)); }, []);

  const normalizeCategory = (raw) => (String(raw || "").toLowerCase() === "material" ? "material" : "service");

  const addBlank = (category) => setLineItems([
    ...lineItems,
    { id: crypto.randomUUID(), name: "", description: "", quantity: 1, rate: 0, unit: "ea",
      taxable: true, line_discount_type: "amount", line_discount_value: 0, category },
  ]);

  const addFromBook = (it) => {
    setLineItems([
      ...lineItems,
      { id: crypto.randomUUID(), name: it.name, description: it.description, quantity: 1,
        rate: it.sale_price, unit: it.unit, taxable: it.taxable,
        line_discount_type: "amount", line_discount_value: 0,
        pricebook_item_id: it.id, category: pickerCategory || normalizeCategory(it.category) },
    ]);
    setPickerCategory(null); setSearch("");
  };

  const update = (id, k, v) => {
    setLineItems(lineItems.map((li) => (li.id === id ? { ...li, [k]: v } : li)));
  };
  const remove = (id) => setLineItems(lineItems.filter((li) => li.id !== id));

  // Group by category — preserve original insertion order within each group.
  const services = lineItems.filter((li) => normalizeCategory(li.category) === "service");
  const materials = lineItems.filter((li) => normalizeCategory(li.category) === "material");

  // Calculations use the full flat list.
  let subtotal = 0, taxable = 0;
  lineItems.forEach((li) => {
    const sub = Number(li.quantity || 0) * Number(li.rate || 0);
    const ld = Number(li.line_discount_value || 0);
    const lineTotal = li.line_discount_type === "percent" ? sub * (1 - ld / 100) : sub - ld;
    const lt = Math.max(lineTotal, 0);
    subtotal += lt;
    if (li.taxable) taxable += lt;
  });
  const invDisc = invDiscType === "percent"
    ? subtotal * (Number(invDiscVal || 0) / 100)
    : Math.min(Number(invDiscVal || 0), subtotal);
  const afterDisc = subtotal - invDisc;
  const taxableAfter = subtotal > 0 ? taxable * (afterDisc / subtotal) : 0;
  const hst = taxableAfter * (hstRate / 100);
  const total = afterDisc + hst;

  const pickerFilter = (p) => {
    if (search && !p.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (pickerCategory === "material") return normalizeCategory(p.category) === "material";
    if (pickerCategory === "service") return normalizeCategory(p.category) !== "material";
    return true;
  };
  const filtered = pricebook.filter(pickerFilter);

  return (
    <div className="space-y-5">
      <SubSection
        label="Services"
        icon={Wrench}
        items={services}
        onUpdate={update}
        onRemove={remove}
        onAddBlank={() => addBlank("service")}
        onOpenPicker={() => setPickerCategory("service")}
        emptyLabel="No services on this invoice yet."
        addLabel="Add service"
        priceBookHref="/pricebook"
        priceBookLabel="Service Price Book"
      />

      <SubSection
        label="Materials"
        icon={Package}
        items={materials}
        onUpdate={update}
        onRemove={remove}
        onAddBlank={() => addBlank("material")}
        onOpenPicker={() => setPickerCategory("material")}
        emptyLabel="No materials on this invoice yet."
        addLabel="Add material"
        priceBookHref="/pricebook"
        priceBookLabel="Material Price Book"
      />

      {/* Totals — stacked rows to match the HCP layout */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md divide-y divide-slate-100 dark:divide-slate-800" data-testid="invoice-totals-block">
        <TotalsRow label="Subtotal" value={fmtCAD(subtotal)} testId="totals-subtotal" />
        <DiscountRow
          invDiscType={invDiscType} setInvDiscType={setInvDiscType}
          invDiscVal={invDiscVal} setInvDiscVal={setInvDiscVal}
          invDisc={invDisc}
        />
        <TotalsRow
          label={
            <div>
              <div className="font-medium text-slate-900 dark:text-slate-100">Tax rate</div>
              <div className="text-xs text-slate-500 dark:text-slate-400">HST ({hstRate}%)</div>
            </div>
          }
          value={fmtCAD(hst)}
          testId="totals-tax"
        />
        <div className="px-5 py-3 flex items-center justify-between" data-testid="totals-total">
          <div className="font-display font-bold text-lg text-slate-900 dark:text-slate-100">Total</div>
          <div className="font-display font-bold text-lg tabular" style={{ color: brand }}>{fmtCAD(total)}</div>
        </div>
        <DepositRow disabled />
      </div>

      {pickerCategory && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 rounded-md max-w-lg w-full max-h-[80vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b border-slate-200 dark:border-slate-800">
              <h3 className="font-semibold">Pick from {pickerCategory === "material" ? "Material" : "Service"} Price Book</h3>
              <button onClick={() => { setPickerCategory(null); setSearch(""); }} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded"><X size={18} /></button>
            </div>
            <div className="p-4 border-b border-slate-200 dark:border-slate-800">
              <input autoFocus value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-md focus:outline-none focus:border-[#0F4C81] bg-white dark:bg-slate-900" />
            </div>
            <div className="flex-1 overflow-auto">
              {filtered.length === 0 && <div className="p-6 text-center text-sm text-slate-400">No matching items in the price book.</div>}
              {filtered.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  data-testid={`pick-item-${p.id}`}
                  onClick={() => addFromBook(p)}
                  className="w-full text-left px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800 border-b border-slate-100 dark:border-slate-800"
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="font-medium text-sm">{p.name}</div>
                      <div className="text-xs text-slate-500">{p.category} · {p.unit}</div>
                    </div>
                    <div className="text-sm font-semibold tabular">{fmtCAD(p.sale_price)}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SubSection({ label, icon: Icon, items, onUpdate, onRemove, onAddBlank, onOpenPicker, emptyLabel, addLabel, priceBookHref, priceBookLabel }) {
  return (
    <div data-testid={`lineitems-section-${label.toLowerCase()}`} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md">
      <div className="px-5 py-3 flex items-center justify-between gap-3 border-b border-slate-100 dark:border-slate-800">
        <div className="flex items-center gap-2">
          <Icon size={16} className="text-slate-500 dark:text-slate-400" />
          <h4 className="font-display font-semibold text-slate-900 dark:text-slate-100">{label}</h4>
        </div>
        <a href={priceBookHref} target="_blank" rel="noopener noreferrer" className="text-xs text-[#0F4C81] hover:underline font-medium">{priceBookLabel}</a>
      </div>

      {items.length === 0 ? (
        <div className="px-5 py-6 text-sm text-slate-400 text-center border-b border-slate-100 dark:border-slate-800">{emptyLabel}</div>
      ) : (
        <>
        {/* Mobile stacked view (< 640px) — full-width fields, tap-friendly */}
        <div className="sm:hidden divide-y divide-slate-200 dark:divide-slate-800">
          {items.map((li, i) => {
            const sub = Number(li.quantity || 0) * Number(li.rate || 0);
            const ld = Number(li.line_discount_value || 0);
            const amt = Math.max(li.line_discount_type === "percent" ? sub * (1 - ld / 100) : sub - ld, 0);
            return (
              <div key={li.id} data-testid={`line-item-mobile-${i}`} className="p-4 space-y-3">
                <input
                  data-testid={`li-name-m-${i}`}
                  value={li.name}
                  onChange={(e) => onUpdate(li.id, "name", e.target.value)}
                  placeholder="Item name"
                  className="w-full px-3 py-3 text-base border border-slate-200 dark:border-slate-700 rounded focus:outline-none focus:border-[#0F4C81] bg-white dark:bg-slate-900 min-h-[44px]"
                />
                <textarea
                  data-testid={`li-desc-m-${i}`}
                  value={li.description || ""}
                  onChange={(e) => onUpdate(li.id, "description", e.target.value)}
                  placeholder="Description — visible to customer on the invoice (optional)"
                  rows={4}
                  className="block w-full box-border px-3 py-2 text-base leading-relaxed text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 rounded focus:outline-none focus:border-[#0F4C81] focus:ring-1 focus:ring-[#0F4C81]/20 resize-y min-h-[96px] bg-white dark:bg-slate-900"
                  style={{ wordBreak: "normal", overflowWrap: "break-word", whiteSpace: "pre-wrap" }}
                />
                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <span className="block text-[11px] uppercase tracking-wider text-slate-500 font-medium mb-1">Qty</span>
                    <input
                      data-testid={`li-qty-m-${i}`}
                      type="number" step="0.01" inputMode="decimal"
                      value={li.quantity}
                      onChange={(e) => onUpdate(li.id, "quantity", e.target.value)}
                      className="w-full px-3 py-3 text-base text-right border border-slate-200 dark:border-slate-700 rounded focus:outline-none focus:border-[#0F4C81] tabular bg-white dark:bg-slate-900 min-h-[44px]"
                    />
                  </label>
                  <label className="block">
                    <span className="block text-[11px] uppercase tracking-wider text-slate-500 font-medium mb-1">Unit price</span>
                    <input
                      data-testid={`li-rate-m-${i}`}
                      type="number" step="0.01" inputMode="decimal"
                      value={li.rate}
                      onChange={(e) => onUpdate(li.id, "rate", e.target.value)}
                      className="w-full px-3 py-3 text-base text-right border border-slate-200 dark:border-slate-700 rounded focus:outline-none focus:border-[#0F4C81] tabular bg-white dark:bg-slate-900 min-h-[44px]"
                    />
                  </label>
                </div>
                <div className="grid grid-cols-[1fr_auto] gap-3 items-end">
                  <label className="block">
                    <span className="block text-[11px] uppercase tracking-wider text-slate-500 font-medium mb-1">Discount</span>
                    <div className="flex gap-2">
                      <input
                        data-testid={`li-disc-m-${i}`}
                        type="number" step="0.01" inputMode="decimal"
                        value={li.line_discount_value}
                        onChange={(e) => onUpdate(li.id, "line_discount_value", e.target.value)}
                        className="flex-1 min-w-0 px-3 py-3 text-base text-right border border-slate-200 dark:border-slate-700 rounded focus:outline-none focus:border-[#0F4C81] tabular bg-white dark:bg-slate-900 min-h-[44px]"
                      />
                      <select
                        value={li.line_discount_type}
                        onChange={(e) => onUpdate(li.id, "line_discount_type", e.target.value)}
                        className="px-2 text-base border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-900 min-h-[44px]"
                      >
                        <option value="amount">$</option><option value="percent">%</option>
                      </select>
                    </div>
                  </label>
                  <label className="flex items-center gap-2 pb-3">
                    <input
                      type="checkbox"
                      checked={li.taxable}
                      onChange={(e) => onUpdate(li.id, "taxable", e.target.checked)}
                      className="w-5 h-5"
                    />
                    <span className="text-sm text-slate-700 dark:text-slate-300">Tax</span>
                  </label>
                </div>
                <div className="flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-800">
                  <button
                    type="button"
                    onClick={() => onRemove(li.id)}
                    className="flex items-center gap-1 px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded min-h-[44px]"
                    data-testid={`li-remove-m-${i}`}
                  >
                    <Trash2 size={16} /> Remove
                  </button>
                  <div className="text-right">
                    <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Amount</div>
                    <div className="text-base font-semibold tabular text-slate-900 dark:text-slate-100">{fmtCAD(amt)}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {/* Desktop table view (>= 640px) — unchanged */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-800">
              <tr>
                <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider text-slate-500 font-medium w-2/5">Name / Description</th>
                <th className="px-3 py-2 text-right text-[11px] uppercase tracking-wider text-slate-500 font-medium">Qty</th>
                <th className="px-3 py-2 text-right text-[11px] uppercase tracking-wider text-slate-500 font-medium">Unit price</th>
                <th className="px-3 py-2 text-right text-[11px] uppercase tracking-wider text-slate-500 font-medium">Disc</th>
                <th className="px-3 py-2 text-center text-[11px] uppercase tracking-wider text-slate-500 font-medium">Tax</th>
                <th className="px-3 py-2 text-right text-[11px] uppercase tracking-wider text-slate-500 font-medium">Amount</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((li, i) => {
                const sub = Number(li.quantity || 0) * Number(li.rate || 0);
                const ld = Number(li.line_discount_value || 0);
                const amt = Math.max(li.line_discount_type === "percent" ? sub * (1 - ld / 100) : sub - ld, 0);
                return (
                  <tr key={li.id} data-testid={`line-item-${i}`} className="border-b border-slate-100 dark:border-slate-800">
                    <td className="px-2 py-3 align-top">
                      <input data-testid={`li-name-${i}`} value={li.name} onChange={(e) => onUpdate(li.id, "name", e.target.value)} placeholder="Item name" className="w-full px-2 py-1.5 text-sm border border-slate-200 dark:border-slate-700 rounded focus:outline-none focus:border-[#0F4C81] bg-white dark:bg-slate-900" />
                      <textarea
                        data-testid={`li-desc-${i}`}
                        value={li.description || ""}
                        onChange={(e) => onUpdate(li.id, "description", e.target.value)}
                        placeholder="Description — visible to customer on the invoice (optional)"
                        rows={3}
                        onFocus={(e) => { e.target.rows = 6; }}
                        onBlur={(e) => { e.target.rows = 3; }}
                        className="w-full mt-2 px-3 py-2 text-sm leading-relaxed text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 rounded focus:outline-none focus:border-[#0F4C81] focus:ring-1 focus:ring-[#0F4C81]/20 resize-y transition-all min-h-[72px] bg-white dark:bg-slate-900"
                      />
                    </td>
                    <td className="px-2 py-3 align-top"><input data-testid={`li-qty-${i}`} type="number" step="0.01" value={li.quantity} onChange={(e) => onUpdate(li.id, "quantity", e.target.value)} className="w-20 px-2 py-1.5 text-sm text-right border border-slate-200 dark:border-slate-700 rounded focus:outline-none focus:border-[#0F4C81] tabular bg-white dark:bg-slate-900" /></td>
                    <td className="px-2 py-3 align-top"><input data-testid={`li-rate-${i}`} type="number" step="0.01" value={li.rate} onChange={(e) => onUpdate(li.id, "rate", e.target.value)} className="w-24 px-2 py-1.5 text-sm text-right border border-slate-200 dark:border-slate-700 rounded focus:outline-none focus:border-[#0F4C81] tabular bg-white dark:bg-slate-900" /></td>
                    <td className="px-2 py-3 align-top">
                      <div className="flex gap-1">
                        <input data-testid={`li-disc-${i}`} type="number" step="0.01" value={li.line_discount_value} onChange={(e) => onUpdate(li.id, "line_discount_value", e.target.value)} className="w-16 px-2 py-1.5 text-sm text-right border border-slate-200 dark:border-slate-700 rounded focus:outline-none focus:border-[#0F4C81] tabular bg-white dark:bg-slate-900" />
                        <select value={li.line_discount_type} onChange={(e) => onUpdate(li.id, "line_discount_type", e.target.value)} className="px-1 text-xs border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-900">
                          <option value="amount">$</option><option value="percent">%</option>
                        </select>
                      </div>
                    </td>
                    <td className="px-2 py-3 text-center align-top"><input type="checkbox" checked={li.taxable} onChange={(e) => onUpdate(li.id, "taxable", e.target.checked)} className="mt-2" /></td>
                    <td className="px-2 py-3 text-right tabular font-medium align-top pt-4">{fmtCAD(amt)}</td>
                    <td className="px-2 py-3 align-top"><button type="button" onClick={() => onRemove(li.id)} className="p-1 text-slate-400 hover:text-red-600 mt-1"><Trash2 size={14} /></button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </>
      )}

      <div className="px-3 py-3 bg-slate-50 dark:bg-slate-900/50 border-t border-slate-200 dark:border-slate-800 flex gap-2">
        <button type="button" onClick={onOpenPicker} className="px-3 py-1.5 text-xs border border-slate-300 dark:border-slate-700 rounded hover:bg-white dark:hover:bg-slate-800 flex items-center gap-1"><Plus size={12} /> From Price Book</button>
        <button type="button" onClick={onAddBlank} data-testid={`add-blank-${label.toLowerCase()}`} className="px-3 py-1.5 text-xs border border-slate-300 dark:border-slate-700 rounded hover:bg-white dark:hover:bg-slate-800 flex items-center gap-1"><Plus size={12} /> {addLabel}</button>
      </div>
    </div>
  );
}

function TotalsRow({ label, value, testId }) {
  return (
    <div className="px-5 py-3 flex items-center justify-between text-sm" data-testid={testId}>
      <div>{label}</div>
      <div className="tabular font-medium text-slate-900 dark:text-slate-100">{value}</div>
    </div>
  );
}

function DiscountRow({ invDiscType, setInvDiscType, invDiscVal, setInvDiscVal, invDisc }) {
  const [open, setOpen] = useState(Number(invDiscVal || 0) > 0);
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="add-discount-btn"
        className="w-full px-5 py-3 flex items-center justify-between text-sm text-[#0F4C81] hover:bg-slate-50 dark:hover:bg-slate-800/50"
      >
        <span className="font-medium">Add discount</span>
        <Plus size={16} />
      </button>
    );
  }
  return (
    <div className="px-5 py-3 flex items-center justify-between gap-4 text-sm" data-testid="discount-row">
      <div className="flex-1">
        <div className="font-medium text-slate-900 dark:text-slate-100">Invoice discount</div>
        <div className="mt-1 flex gap-2">
          <input data-testid="inv-discount-value" type="number" step="0.01" value={invDiscVal} onChange={(e) => setInvDiscVal(e.target.value)} className="w-28 px-2 py-1 text-sm border border-slate-300 dark:border-slate-700 rounded focus:outline-none focus:border-[#0F4C81] tabular bg-white dark:bg-slate-900" />
          <select data-testid="inv-discount-type" value={invDiscType} onChange={(e) => setInvDiscType(e.target.value)} className="px-2 text-sm border border-slate-300 dark:border-slate-700 rounded bg-white dark:bg-slate-900">
            <option value="amount">$ Amount</option><option value="percent">% Percent</option>
          </select>
          <button type="button" onClick={() => { setInvDiscVal(0); setOpen(false); }} className="text-xs text-slate-400 hover:text-red-600 px-2">Remove</button>
        </div>
      </div>
      <div className="tabular font-medium">-{fmtCAD(invDisc)}</div>
    </div>
  );
}

function DepositRow({ disabled }) {
  return (
    <button
      type="button"
      disabled={disabled}
      title={disabled ? "Deposit tracking is not yet implemented" : ""}
      data-testid="add-deposit-btn"
      className={`w-full px-5 py-3 flex items-center justify-between text-sm ${disabled ? "text-slate-300 dark:text-slate-600 cursor-not-allowed" : "text-[#0F4C81] hover:bg-slate-50 dark:hover:bg-slate-800/50"}`}
    >
      <span className="font-medium">Add deposit</span>
      <Plus size={16} />
    </button>
  );
}
